import DOMPurify from 'dompurify';

/**
 * Sanitizes HTML content to prevent XSS attacks
 * @param {string} dirty - The potentially unsafe HTML string
 * @param {Object} config - Optional DOMPurify configuration
 * @returns {string} - Sanitized HTML string safe for rendering
 */
export const sanitizeHtml = (dirty, config = {}) => {
	if (!dirty || typeof dirty !== 'string') {
		return '';
	}

	// Default configuration - allows comprehensive HTML formatting including styles
	const defaultConfig = {
		ALLOWED_TAGS: [
			'p', 'br', 'strong', 'em', 'u', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
			'ul', 'ol', 'li', 'a', 'span', 'div', 'img', 'table', 'thead', 'tbody',
			'tr', 'td', 'th', 'blockquote', 'code', 'pre', 'b', 'i', 's', 'strike',
			'section', 'article', 'aside', 'nav', 'header', 'footer', 'main',
			'figure', 'figcaption', 'hr', 'dl', 'dt', 'dd', 'small', 'sub', 'sup',
			'mark', 'del', 'ins', 'abbr', 'address', 'cite', 'q', 'time'
		],
		ALLOWED_ATTR: [
			'href', 'src', 'alt', 'title', 'class', 'id', 'target', 'rel',
			'style', 'width', 'height', 'align', 'valign', 'border', 'cellpadding',
			'cellspacing', 'colspan', 'rowspan', 'type', 'start', 'value',
			'data-*', 'aria-*', 'role'
		],
		ALLOW_DATA_ATTR: true,
		ALLOW_ARIA_ATTR: true,
		ADD_ATTR: ['style'], // Explicitly allow style attribute
		...config
	};

	return DOMPurify.sanitize(dirty, defaultConfig);
};

/**
 * Creates a sanitized object safe for dangerouslySetInnerHTML
 * @param {string} html - The HTML string to sanitize
 * @returns {Object} - Object with __html property containing sanitized HTML
 */
export const createSafeHtml = (html) => {
	return {
		__html: sanitizeHtml(html)
	};
};
